//react
import { useEffect, useState } from "react";

//CSS
import "./SurveyNational.css";
import "../Card.css";

//antd
import { Typography, Popover } from "antd";
import { InfoCircleOutlined, DownloadOutlined } from "@ant-design/icons";

//utils
import {
  TOOLTIP_STYLE,
  LEGEND_CONFIG,
} from "../../../utils/constants/graphConfigs";
import { formatNumber } from "../../../utils/functions/formatNumber";

//React echarts
import ReactECharts from "echarts-for-react";

//colors
import { getTwoLineColors } from "../../../utils/functions/colors";

//components
import ErrorLoading from "../../404/ErrorLoading";
import StyledSelect from "../StyledSelect";
import CenterSpin from "../../misc/spin";

//language
import { useCurrentLanguage } from "../../../hooks/useCurrentLanguage";

//html2canvas
import html2canvas from "html2canvas";

const { Paragraph, Text, Title } = Typography;

/**
 *
 * @param {string} country
 * @returns {JSX} JSX -- Complete Card component contents
 */
export default function SurveyNational({ data, questions, title }) {
  //language
  const { language } = useCurrentLanguage();

  //useState - options
  const [options, setOptions] = useState(null);

  //useState - selected question
  const [selectedQuestion, setSelectedQuestion] = useState(0);

  //useState - selected question label
  const [selectedQuestionLbl, setSelectedQuestionLbl] = useState("");

  //useState - selected option (for select component)
  const [selectedOption, setSelectedOption] = useState("1");

  //useState - loading
  const [loading, setLoading] = useState(true);

  //useState - tempTitle
  const [tempTitle, setTempTitle] = useState("");

  //useEffect
  useEffect(() => {
    if (data.length > 0) {
      let tempSelectedQuestion = tempTitle === title ? selectedQuestion : 0;
      setSelectedQuestion(tempSelectedQuestion);
      const opts = generateGraphOptions(data, tempSelectedQuestion);

      setSelectedQuestionLbl(data[tempSelectedQuestion].question);
      setTempTitle(title);
      setOptions(opts);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, selectedQuestion, selectedOption, language, title]);

  const generateGraphOptions = (data, selectedQuestion) => {
    const graphType = data[selectedQuestion].graphType;
    const commonOptions = {
      colors: [
        "#C3E0E0",
        "#8FB4B5",
        "#FE7D73",
        "#F1F8E1",
        "#F8B9AB",
        "#FE8980",
      ],
      textStyle: {
        fontFamily: "Open Sans",
      },
      tooltip: {
        show: true,
        ...TOOLTIP_STYLE,
        formatter: (params) =>
          `<span>${params.name}</span><br/> ${params.value.toFixed(1)}%`,
      },
      grid: {
        left: "5%",
        right: "5%",
        bottom: data[selectedQuestion].marginBottom || "60px",
      },
    };

    const categoryAxisLabel = (dataArray) => ({
      interval: 0,
      height: 20,
      overflow: "break",
      fontSize: dataArray.length < 4 ? "14px" : "11px",
      rotate: data[selectedQuestion].rotate ? 45 : 0,
      maxHeight: "20px",
      width: dataArray.length < 3 ? 400 : 100,
    });

    const valueAxisLabel = {
      formatter: "{value}%",
    };

    /*const seriesLabelFormatter = {
      show: true,
      fontSize: "11px",
      fontFamily: "Open Sans",
      formatter: (params) => `${params.value}%`,
    };*/

    switch (graphType) {
      case "bar":
        return {
          ...commonOptions,
          xAxis: [
            {
              type: "category",
              nameLocation: "middle",
              axisTick: { show: false },
              data: data[selectedQuestion].data[0],
              axisLabel: categoryAxisLabel(data[selectedQuestion].data[0]),
            },
          ],
          yAxis: [
            {
              type: "value",
              showMaxLabel: "false",
              axisLabel: valueAxisLabel,
            },
          ],
          legend: {
            data: data[selectedQuestion].barLegend
              ? [data[selectedQuestion].barLegend]
              : [""],
            textStyle: {
              fontSize: 16,
            },
          },
          series: [
            {
              type: "bar",
              name: data[selectedQuestion].barLegend || "",
              itemStyle: { borderRadius: [20, 20, 0, 0] },
              data: data[selectedQuestion].data[1],
            },
          ],
        };
      case "h_bar":
        return {
          ...commonOptions,
          yAxis: {
            type: "category",
            nameLocation: "middle",
            axisTick: { show: false },
            data: data[selectedQuestion].data[0].map((label) => {
              let labels = ["Inmigración Irregular", "Irregular immigration"];
              if (labels.includes(label)) {
                return {
                  value: label,
                  textStyle: {
                    fontWeight: "bold",
                  },
                };
              } else {
                return label;
              }
            }),
            axisLabel: {
              interval: 0,
              width: 100,
              overflow: "break",
            },
          },
          xAxis: [
            {
              type: "value",
              nameLocation: "middle",
              axisLabel: valueAxisLabel,
            },
          ],
          series: [
            {
              type: "bar",
              itemStyle: { borderRadius: [0, 20, 20, 0] },
              data: data[selectedQuestion].data[1],
            },
          ],
        };
      case "line":
        return {
          ...commonOptions,
          color: getTwoLineColors(),
          xAxis: {
            type: "category",
            data: data[selectedQuestion].data[0],
            axisTick: {
              show: false,
            },
            axisLabel: {
              showMaxLabel: true,
              margin: 10,
              interval: 2,
            },
          },
          yAxis: {
            type: "value",
            axisLine: {
              show: true,
            },
            axisLabel: {
              margin: 20,
              fontSize: 14,
              formatter: "{value}%",
            },
          },
          tooltip: {
            trigger: "axis",
            ...TOOLTIP_STYLE,
            formatter: function (params) {
              return `
                                <div>
                                    <span>${params[0].axisValueLabel}</span>
                                    <div>
                                        <div style='display: flex; flex-direction: row; justify-content: space-between;'> 
                                            <div style='margin-right: 15px'>${
                                              params[0].marker
                                            } ${params[0].seriesName}</div>
                                            <div><b>${formatNumber(
                                              params[0].data,
                                              language
                                            )}%</b></div>
                                        </div>
                                        <div style='display: flex; flex-direction: row; justify-content: space-between;'>
                                            <div style='margin-right: 15px'>${
                                              params[1].marker
                                            } ${params[1].seriesName}</div>
                                            <div><b>${formatNumber(
                                              params[1].data,
                                              language
                                            )}%</b></div>
                                        </div>
                                    </div>
                                </div>
                            `;
            },
          },
          legend: {
            ...LEGEND_CONFIG,
            data:
              language === "en"
                ? ["Favourable", "Unfavourable"]
                : ["Favorable", "Desfavorable"],
          },
          grid: {
            left: "60px",
            top: "15%",
            height: "76%",
            width: "90%",
          },
          series: makeSeries(data[selectedQuestion].data),
        };
      case "g_h_bar":
      case "s_h_bar":
        return {
          ...commonOptions,
          color: ["#305EA9", "#6CCDEF", "#8DBA38", "#F4DB4E"],
          tooltip: {
            trigger: "axis",
            valueFormatter: (value) => {
              return value.toFixed(1) + "%";
            },
            axisPointer: {
              type: "shadow",
            },
            formatter: (props) => {
              const divs = [];

              divs.push("<span>" + props[0].axisValue + "</span></br>");
              props.forEach((line) => {
                divs.push(
                  "<div>" +
                    line.marker +
                    " " +
                    line.seriesName +
                    ": " +
                    line.data.toFixed(1) +
                    "%</div>"
                );
              });
              return divs.join("");
            },
          },
          legend: {
            top: "2%",
            left: "10%",
            type: "scroll",
            data: data[selectedQuestion].data[0],
          },
          grid: {
            top: "9%",
            left: "4%",
            right: "4%",
            containLabel: true,
          },
          xAxis: {
            type: "value",
            max: graphType === "s_h_bar" ? 100 : null,
            nameTextStyle: {
              fontFamily: ["Open Sans", "sans-serif"],
              fontSize: 15,
            },
            axisLabel: valueAxisLabel,
          },
          yAxis: {
            type: "category",
            data: data[selectedQuestion].data[1].map((label) => {
              let labels = ["Inmigración", "Inmigration"];
              if (labels.includes(label)) {
                return {
                  value: label,
                  textStyle: {
                    fontWeight: "bold",
                  },
                };
              } else {
                return label;
              }
            }),
            nameTextStyle: {
              fontFamily: ["Open Sans", "sans-serif"],
              fontSize: 15,
            },
            axisLabel: {
              interval: 0,
              width: 200,
              overflow: "break",
            },
          },
          series: makeSeriesGroupBar(data[selectedQuestion].data[2]),
        };
      case "g_v_bar":
        return {
          ...commonOptions,
          color: ["#305EA9", "#6CCDEF", "#8DBA38", "#F4DB4E"],
          tooltip: {
            trigger: "axis",
            valueFormatter: (value) => {
              return value.toFixed(1) + "%";
            },
            axisPointer: {
              type: "shadow",
            },
            formatter: (props) => {
              const divs = [];

              divs.push("<span>" + props[0].axisValue + "</span></br>");
              props.forEach((line) => {
                divs.push(
                  "<div>" +
                    line.marker +
                    " " +
                    line.seriesName +
                    ": " +
                    line.data.toFixed(1) +
                    "%</div>"
                );
              });
              return divs.join("");
            },
          },
          legend: {
            top: "2%",
            left: "10%",
            type: "scroll",
            data:
              data[selectedQuestion].hasOptions === true &&
              selectedOption === "2"
                ? data[selectedQuestion].data2[0]
                : data[selectedQuestion].data[0],
          },
          grid: {
            top: "9%",
            left: "5%",
            right: "3%",
            bottom: data[selectedQuestion].marginBottom || "60px",
          },
          yAxis: {
            type: "value",
            max: null,
            nameTextStyle: {
              fontFamily: ["Open Sans", "sans-serif"],
              fontSize: 15,
            },
            axisLabel: valueAxisLabel,
          },
          xAxis: {
            type: "category",
            data:
              data[selectedQuestion].hasOptions === true &&
              selectedOption === "2"
                ? data[selectedQuestion].data2[1]
                : data[selectedQuestion].data[1],
            nameTextStyle: {
              fontFamily: ["Open Sans", "sans-serif"],
              fontSize: 15,
            },
            axisLabel: {
              interval: 0,
              width: 100,
              overflow: "break",
            },
          },
          series: makeSeriesGroupBar(
            data[selectedQuestion].hasOptions === true && selectedOption === "2"
              ? data[selectedQuestion].data2[2]
              : data[selectedQuestion].data[2]
          ),
        };
      case "pie":
        return {
          ...commonOptions,
          color: ["#305EA9", "#6CCDEF", "#8DBA38", "#F4DB4E"],
          tooltip: {
            trigger: "item",
            formatter: "{b}: {c} ({d}%)",
          },
          legend: {
            orient: "vertical",
            left: "left",
            data: data[selectedQuestion].data[0],
          },
          series: [
            {
              name: data[selectedQuestion].data[0][0],
              type: "pie",
              radius: "70%",
              center: ["50%", "50%"],
              data: data[selectedQuestion].data,
              label: {
                show: true,
                position: "outside",
                formatter: "{b}: {d}%",
              },
            },
          ],
          grid: {
            top: "20px%",
            left: "20px",
            right: "20px",
            bottom: "60px",
          },
        };
      case "area":
        return {
          ...commonOptions,
          color: ["#305EA9", "#6CCDEF", "#8DBA38", "#F4DB4E"],
          tooltip: {
            trigger: "axis",
            axisPointer: {
              type: "cross",
              label: {
                backgroundColor: "#6a7985",
              },
            },
          },
          legend: {
            data: data[selectedQuestion].data[1],
          },
          toolbox: {
            feature: {
              saveAsImage: {},
            },
          },
          grid: {
            left: "3%",
            right: "4%",
            bottom: "3%",
            containLabel: true,
          },
          xAxis: [
            {
              type: "category",
              boundaryGap: false,
              data: data[selectedQuestion].data[0],
            },
          ],
          yAxis: [
            {
              type: "value",
            },
          ],
          series: data[selectedQuestion].data[1].map((statement, i) => {
            return {
              name: statement,
              type: "line",
              stack: "Total",
              areaStyle: {},
              emphasis: {
                focus: "series",
              },
              data: data[selectedQuestion].data[2][i],
            };
          }),
        };
      default:
        return {};
    }
  };

  const makeSeries = (arr) => {
    return [
      {
        name: language === "en" ? "Favourable" : "Favorable",
        type: "line",
        lineStyle: {
          width: 3,
        },
        data: arr[1],
      },
      {
        name: language === "en" ? "Unfavourable" : "Desfavorable",
        type: "line",
        lineStyle: {
          width: 3,
        },
        data: arr[2],
      },
    ];
  };

  function makeSeriesGroupBar(arr) {
    return arr.map((item, i) => {
      return {
        name: data[selectedQuestion].data[0][i],
        type: "bar",
        itemStyle: {
          borderRadius:
            data[selectedQuestion].graphType === "s_h_bar"
              ? [0, 0, 0, 0]
              : data[selectedQuestion].graphType === "g_h_bar"
              ? [0, 20, 20, 0]
              : [20, 20, 0, 0],
        },
        stack: data[selectedQuestion].graphType === "s_h_bar" ? "total" : false,
        emphasis: {
          focus: "series",
        },
        data: item,
      };
    });
  }

  function EchartsContent() {
    try {
      return (
        <ReactECharts
          option={options}
          style={{ height: "100%", width: "100%" }}
        />
      );
    } catch (error) {
      return <ErrorLoading language={language} />;
    }
  }

  const triggerDownload = () => {
    const graph = document
      .getElementById(title + "-container")
      .getElementsByClassName("echarts-for-react")[0];

    const cardContainer = document.createElement("div");
    cardContainer.style.cssText = data[selectedQuestion].isLarge
      ? "width: 100%; height: 970px; position: absolute; padding-left: 25px; padding-right: 25px; padding-top: 30px; z-index: 100; background-color: 'white'; left: -1000px;"
      : "width: 800px; height: 620px; position: absolute; padding-left: 25px; padding-right: 25px; padding-top: 30px; z-index: 100; background-color: 'white'; left: -1000px;";
    cardContainer.appendChild(graph);

    html2canvas(cardContainer, { allowTaint: false }).then((canvas) => {
      const link = document.createElement("a");
      link.download =
        language === "en"
          ? `Surveys_${title}_q${selectedQuestion + 1}`
          : `Encuestas_${title}_p${selectedQuestion + 1}`;
      link.href = canvas.toDataURL();
      link.target = "_blank";
      link.click();
    });
  };

  const handleQuestionChange = (value) => {
    const index = data.findIndex((item) => item.id === value);
    if (index === selectedQuestion) return;
    setLoading(true);
    setSelectedQuestion(index);
  };

  const handleOptionChange = (value) => setSelectedOption(value);

  const openInNewTab = (url) => {
    const newWindow = window.open(url, "_blank", "noopener,noreferrer");
    if (newWindow) newWindow.opener = null;
  };

  return (
    <div className="card-container container" id={title + "-container"}>
      <div className="sn-card-left-top">
        <div className="sn-card-title-container">
          <Title
            level={3}
            style={{ color: "var(--blue-primary)", textAlign: "left" }}
          >
            {tempTitle}
          </Title>
          <div className="sn-card-button-container">
            <button
              onClick={triggerDownload}
              className={"downloadButton"}
              style={{
                border: "2px var(--blue-primary) solid",
                backgroundColor: "var(--blue-primary)",
                color: "white",
                borderRadius: "40px",
                cursor: "pointer",
                marginRight: "10px",
                minWidth: "130px",
              }}
            >
              <DownloadOutlined style={{ marginRight: "10px" }} />
              {language === "en" ? "Download" : "Descargar"}
            </button>
            <Popover
              content={
                <div
                  style={{
                    width: "500px",
                    padding: "5px 16px 4px",
                    textAlign: "justify",
                  }}
                >
                  <Text>
                    {data.length > 0 ? data[selectedQuestion].description : ""}
                  </Text>
                </div>
              }
              title={
                language === "en"
                  ? "Survey description"
                  : "Descripción de la encuesta"
              }
              placement="leftTop"
            >
              <InfoCircleOutlined
                style={{
                  fontSize: "20px",
                  color: "var(--blue-primary)",
                  marginRight: "10px",
                }}
              />
            </Popover>
          </div>
        </div>
        {questions.length > 0 && (
          <div className="">
            <StyledSelect
              selectedValue={selectedQuestionLbl}
              changeHandler={handleQuestionChange}
              options={questions}
              placeHolderText={"Selecciona una pregunta"}
              labelAttributeName={"label"}
              valueAttributeName={"code"}
              ignoreTranslation={true}
              language={language}
              isDisabled={false}
            />
          </div>
        )}
      </div>
      {data.length > 0 && data[selectedQuestion].hasOptions && (
        <div className="">
          <StyledSelect
            selectedValue={selectedOption}
            changeHandler={handleOptionChange}
            options={data[selectedQuestion].options}
            placeHolderText={"Selecciona una opción"}
            labelAttributeName={"label"}
            valueAttributeName={"code"}
            ignoreTranslation={true}
            language={language}
            isDisabled={false}
          />
        </div>
      )}
      {options !== null && (
        <div
          id={title + "perception-graph"}
          className={
            data.length > 0 && data[selectedQuestion].isLarge
              ? "set-height-large card-graph"
              : "set-height-small card-graph"
          }
        >
          {loading ? (
            <div style={{ height: "100%" }}>
              <CenterSpin />
            </div>
          ) : (
            <EchartsContent />
          )}
        </div>
      )}
      {data.length > 0 && data[selectedQuestion].legendLabel && (
        <div style={{ textAlign: "center" }}>
          <p>{data[selectedQuestion].legendLabel}</p>
        </div>
      )}
      <div className="source-container">
        <Paragraph
          className="gbi"
          style={{ fontSize: "13px", marginBottom: "0px" }}
        >
          {data.length > 0 && (
            <span>
              {data[selectedQuestion].link && (
                <span
                  onClick={() =>
                    openInNewTab(data[selectedQuestion].link || "")
                  }
                  className="source-link"
                >
                  {language === "en" ? "Source: " : "Fuente: "}
                </span>
              )}
              {data[selectedQuestion].source || ""}
            </span>
          )}
        </Paragraph>
      </div>
      <div id={title + "-container-dc"}></div>
    </div>
  );
}
